import { CardTitle } from './ui/card';
import { Button } from './ui/Button';
import { useArrivalReceiptStore } from 'store/arrivalReceiptStore';
import { formatDateTime } from 'utils/formatDate';
import { cn } from 'utils/classnames';
import { useAppStore } from '../store';
import { SCREEN_TYPES } from 'utils/constants';
import { MoveToPRScreen } from './MoveToPRScreen';

const getDispositionColor = (disposition?: string): string => {
  if (!disposition) return 'bg-slate-100 text-slate-600';
  const d = disposition.toLowerCase();
  if (d.includes('stock') || d.includes('good')) return 'bg-green-100 text-green-700';
  if (d.includes('return') || d.includes('reject')) return 'bg-red-100 text-red-700';
  if (d.includes('hold') || d.includes('review')) return 'bg-yellow-100 text-yellow-700';
  return 'bg-blue-100 text-blue-700';
};

const Field = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
    <span className="text-sm font-semibold text-slate-600">{label}</span>
    <span className="break-all text-sm text-slate-900">{value ?? 'N/A'}</span>
  </div>
);

export const ReceivingDisposition = () => {
  const { currentArrivalReceipt, setCurrentArrivalReceipt } = useArrivalReceiptStore();
  const { setCurrentScreen } = useAppStore();

  const handleConfirm = (): void => {
    setCurrentArrivalReceipt(null);
    setCurrentScreen(SCREEN_TYPES.RECEIVING);
  };

  const isProblem =
    currentArrivalReceipt?.responseState === 'problem' ||
    currentArrivalReceipt?.responseState === 'exception';

  const handleCancel = (): void => {
    setCurrentScreen(SCREEN_TYPES.RECEIVING);
    setCurrentArrivalReceipt(null);
  };

  if (isProblem) {
    return (
      <MoveToPRScreen
        responseState={
          currentArrivalReceipt?.responseState === 'exception' ? 'Exception' : 'Problem'
        }
        responseMsg={currentArrivalReceipt?.responseMsg}
        trackingId={currentArrivalReceipt?.tracking_id ?? ''}
        handleCancel={handleCancel}
      />
    );
  }

  return (
    <div className="space-y-4">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900">
        Disposition
      </CardTitle>

      {currentArrivalReceipt?.arrival_disposition && (
        <div className="text-center">
          <div
            className={cn(
              'inline-block rounded-full px-4 py-2 text-sm font-semibold uppercase tracking-[0.6px] shadow-sm',
              getDispositionColor(currentArrivalReceipt.arrival_disposition)
            )}
          >
            {currentArrivalReceipt.arrival_disposition}
          </div>
        </div>
      )}

      <div className="space-y-1 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <Field label="Transaction #" value={currentArrivalReceipt?.transaction_id} />
        <Field label="RMA ID" value={currentArrivalReceipt?.rma_id} />
        <Field label="Priority ID" value={currentArrivalReceipt?.priority_id} />
        <Field label="Vendor ID" value={currentArrivalReceipt?.vendor_id} />
        <Field label="Vendor Name" value={currentArrivalReceipt?.vendor_name} />
        <Field label="Customer ID" value={currentArrivalReceipt?.customer_id} />
        <Field label="Invoice ID" value={currentArrivalReceipt?.invoice_id} />
        <Field label="Receipt Status" value={currentArrivalReceipt?.receipt_status} />
        <Field label="Expected Lines" value={currentArrivalReceipt?.expected_line_cnt} />
        <Field
          label="Vendor Master Missing"
          value={currentArrivalReceipt?.vendor_master_missing_cnt}
        />
        <Field
          label="Exception Process Count"
          value={currentArrivalReceipt?.exception_process_cnt}
        />
        <Field
          label="Arrival Started"
          value={formatDateTime(currentArrivalReceipt?.arrival_started_datetime)}
        />
        <Field
          label="Arrival Finished"
          value={formatDateTime(currentArrivalReceipt?.arrival_finished_datetime)}
        />
        <Field
          label="Target Grading Confirmed"
          value={formatDateTime(currentArrivalReceipt?.target_grading_confirmed_datetime)}
        />
      </div>
      <div className="buttons-container !justify-center">
        <Button
          type="button"
          onClick={handleConfirm}
          className="h-12 w-full rounded-xl bg-blue-600 px-10 text-base font-bold text-white shadow-[0_4px_12px_rgba(0,123,255,0.35)] hover:bg-blue-700"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
